import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, PenLine, ShieldCheck, Download, Stamp } from "lucide-react";

const usePad = (lineWidth) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [has, setHas] = useState(false);
  const pos = (e) => { const c = ref.current.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: (t.clientX - c.left) * (ref.current.width / c.width), y: (t.clientY - c.top) * (ref.current.height / c.height) }; };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineWidth = lineWidth; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a"; ctx.lineTo(p.x, p.y); ctx.stroke(); setHas(true); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = ref.current; if (!c) { setHas(false); return; } c.getContext("2d").clearRect(0, 0, c.width, c.height); setHas(false); };
  const handlers = { onMouseDown: start, onMouseMove: move, onMouseUp: end, onMouseLeave: end, onTouchStart: start, onTouchMove: move, onTouchEnd: end };
  return { ref, has, clear, handlers, data: () => ref.current.toDataURL("image/png") };
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

export default function SignAgreement() {
  const { token } = useParams();
  const [agr, setAgr] = useState(null);
  const [name, setName] = useState("");
  const [acks, setAcks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [ini, setIni] = useState("");
  const sig = usePad(2);
  const iniPad = usePad(3);

  useEffect(() => { axios.get(`${API}/agreements/${token}`).then((r) => { setAgr(r.data); setName(r.data.debtor_name || ""); }).catch(() => setAgr({ error: true })); }, [token]);

  const saveIni = () => { if (!iniPad.has) { toast.error("Teken eerst uw paraaf"); return; } setIni(iniPad.data()); toast.success("Paraaf opgeslagen — zet hem nu op iedere pagina"); };
  const resetIni = () => { setIni(""); setAcks({}); iniPad.clear(); };
  const stamp = (i) => { if (!ini) { toast.error("Teken en bewaar eerst uw paraaf bovenaan"); return; } setAcks((a) => ({ ...a, [i]: !a[i] })); };
  const stampAll = () => { if (!ini) { toast.error("Teken en bewaar eerst uw paraaf bovenaan"); return; } setAcks(Object.fromEntries((agr.pages || []).map((_, i) => [i, true]))); };

  const submit = async () => {
    if (!name.trim()) return toast.error("Vul uw naam in");
    if (!ini) return toast.error("Zet eerst uw paraaf");
    const pageAcks = (agr.pages || []).map((_, i) => i).filter((i) => acks[i]);
    if (pageAcks.length < (agr.pages || []).length) return toast.error("Parafeer elke pagina");
    if (!sig.has) return toast.error("Plaats uw handtekening");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/agreements/${token}/sign`, { signer_name: name, signature_data: sig.data(), initials_data: ini, page_acks: pageAcks });
      toast.success("Bedankt! Uw handtekening is ontvangen.");
      setAgr({ ...agr, status: "getekend", complete: r.data.complete !== false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Ondertekenen mislukt"); }
    finally { setSubmitting(false); }
  };

  if (!agr) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400">Laden...</div>;
  if (agr.error) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Overeenkomst niet gevonden.</div>;

  const signed = agr.status === "getekend";

  return (
    <div className="min-h-screen bg-[#F7F5EF]">
      <div className="bg-[#0F1E3D] border-b-2 border-[#C9A227] px-4 py-4 mb-6">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white p-1 flex items-center justify-center ring-1 ring-[#C9A227]/60"><img src="/logo.png" alt="" className="h-full w-full object-contain" /></div>
          <div><div className="font-heading font-bold text-white">Praktisch-Debiteurenbeheer</div><div className="text-xs text-[#C9A227]">Digitale ondertekening</div></div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto space-y-5 px-4 pb-8">

        {signed ? (
          <Card className="p-8 text-center" data-testid="sign-done">
            <CheckCircle2 className="h-12 w-12 text-[#C9A227] mx-auto mb-3" />
            <h1 className="font-heading text-xl font-bold text-[#0F1E3D]">{agr.complete === false ? "Uw handtekening is ontvangen" : "Overeenkomst getekend"}</h1>
            <p className="text-slate-500 mt-1" data-testid="sign-done-text">{agr.complete === false
              ? `Het document is definitief zodra alle ${agr.signers_total || ""} partijen hebben getekend. U ontvangt dan de getekende PDF per e-mail.`
              : "Dank u wel. Een kopie is vastgelegd bij de beheerder."}</p>
            {agr.complete !== false && <a href={`${API}/agreements/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="sign-download-pdf"
               className="inline-flex items-center gap-2 mt-4 bg-[#0F1E3D] hover:bg-[#1B2D52] text-white px-4 py-2 rounded-lg text-sm font-medium">
              <Download className="h-4 w-4" /> Download getekende PDF
            </a>}
          </Card>
        ) : (
          <>
            <Card className="p-6" data-testid="sign-document">
              <h1 className="font-heading text-2xl font-bold text-[#0F1E3D] mb-1">{agr.title}</h1>
              {agr.signers_total > 1 && <p className="text-xs text-slate-500 mb-4" data-testid="sign-progress">{agr.signers_done} van {agr.signers_total} partijen hebben getekend</p>}
              {!(agr.signers_total > 1) && <div className="mb-3" />}
              <div className="mb-5 rounded-xl border border-[#C9A227]/50 bg-[#C9A227]/5 p-4" data-testid="sign-initials-box">
                <Label className="flex items-center gap-2 text-[#0F1E3D]"><Stamp className="h-4 w-4 text-[#C9A227]" /> Stap 1 — Uw paraaf</Label>
                {ini ? (
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <img src={ini} alt="Paraaf" className="h-12 bg-white border rounded-md px-2" data-testid="sign-initials-preview" />
                    <button className="text-xs text-slate-500 underline" onClick={resetIni} data-testid="sign-initials-reset">Opnieuw tekenen</button>
                    <Button size="sm" variant="outline" className="ml-auto border-[#C9A227]/70" onClick={stampAll} data-testid="sign-initials-all">Parafeer alle pagina's</Button>
                  </div>
                ) : (
                  <div className="mt-2 flex flex-col sm:flex-row sm:items-end gap-3">
                    <canvas ref={iniPad.ref} width={240} height={100} className="border rounded-lg bg-white w-full sm:w-60 h-24 touch-none" {...iniPad.handlers} data-testid="sign-initials-canvas" />
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52]" onClick={saveIni} data-testid="sign-initials-save">Paraaf bewaren</Button>
                      <button className="text-xs text-slate-500 underline" onClick={iniPad.clear}>Wissen</button>
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">Uw paraaf komt onderaan iedere pagina van het getekende document.</p>
              </div>
              {(agr.pages || []).map((pg, i) => (
                <div key={i} className="mb-4 pb-4 border-b last:border-0" data-testid={`sign-page-${i}`}>
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{pg}</pre>
                  <div className="flex items-center justify-between gap-3 mt-3 bg-slate-50 rounded-lg p-2">
                    <span className="text-xs text-slate-500">Pagina {i + 1} van {agr.pages.length} — gelezen en voor akkoord geparafeerd</span>
                    {acks[i] && ini ? (
                      <button onClick={() => stamp(i)} title="Paraaf verwijderen" data-testid={`sign-ack-${i}`} data-stamped="true"><img src={ini} alt="Paraaf" className="h-9 bg-white border border-[#C9A227] rounded-md px-1" /></button>
                    ) : (
                      <Button size="sm" variant="outline" className="border-[#C9A227]/70 text-[#0F1E3D]" onClick={() => stamp(i)} data-testid={`sign-ack-${i}`}><Stamp className="h-3.5 w-3.5 mr-1 text-[#C9A227]" /> Paraaf zetten</Button>
                    )}
                  </div>
                </div>
              ))}
            </Card>

            <Card className="p-6 space-y-4">
              <div><Label>Volledige naam</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="sign-name-input" /></div>
              <div>
                <Label className="flex items-center gap-2"><PenLine className="h-4 w-4" /> Stap 2 — Handtekening</Label>
                <canvas ref={sig.ref} width={600} height={160} className="border rounded-lg bg-white w-full touch-none mt-1" {...sig.handlers} data-testid="sign-canvas" />
                <button className="text-xs text-slate-500 underline mt-1" onClick={sig.clear} data-testid="sign-clear">Wissen</button>
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Uw naam, handtekening, tijdstip en IP-adres worden vastgelegd als rechtsgeldig bewijs van ondertekening.</p>
              <Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52]" onClick={submit} disabled={submitting} data-testid="sign-submit">
                {submitting ? "Bezig..." : "Rechtsgeldig ondertekenen"}
              </Button>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
